import React from 'react';
import {
  DevicePhoneMobileIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  MapPinIcon,
  PhoneIcon,
} from '@heroicons/react/24/outline';
import { useLanguage } from '../LanguageContext';

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.rightoz.user';
const IOS_APP_URL = 'https://apps.apple.com/in/app/rightoz-consumer/id6763323665';
const WEB_APP_URL = 'https://www.consumer.rightoz.in/';
const INSTAGRAM_URL = 'https://www.instagram.com/official_rightoz/';
const INSTAGRAM_REEL_URL = 'https://www.instagram.com/p/DdOiD3eMQdM/';

const quickLinksEn = [
  { name: 'Follow Your Milk', href: '/#journey' },
  { name: 'Watch the Video', href: '/#video' },
  { name: 'Apps', href: '/#app-download-buttons' },
  { name: 'Join as Producer', href: '/join/producer' },
  { name: 'Join as Collector', href: '/join/collector' },
  { name: 'Join as Distributor', href: '/join/distributor' },
  { name: 'Join as Logistics', href: '/join/logistic' },
];

const quickLinksHi = [
  { name: 'अपने दूध का सफ़र', href: '/#journey' },
  { name: 'वीडियो देखें', href: '/#video' },
  { name: 'ऐप', href: '/#app-download-buttons' },
  { name: 'उत्पादक के रूप में जुड़ें', href: '/join/producer' },
  { name: 'संग्रहकर्ता के रूप में जुड़ें', href: '/join/collector' },
  { name: 'वितरक के रूप में जुड़ें', href: '/join/distributor' },
  { name: 'लॉजिस्टिक्स के रूप में जुड़ें', href: '/join/logistic' },
];

const Footer: React.FC = () => {
  const { language } = useLanguage();
  const isHindi = language === 'hi';
  const quickLinks = isHindi ? quickLinksHi : quickLinksEn;
  const currentYear = new Date().getFullYear();

  return (
    <footer id="contact" className="bg-[var(--color-ink)] text-white">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[1.15fr_0.85fr_0.85fr]">
          <div>
            <img src="/logo.png" alt="Rightoz Logo" className="h-12 w-auto" />
            <p className="mt-6 max-w-md text-sm leading-7 text-[#d7e2d0]">
              {isHindi
                ? 'Rightoz ताज़े दूध, किसान साझेदारी, सुरक्षित रख-रखाव और सुविधाजनक सदस्यता के साथ अधिक पारदर्शी डेयरी व्यवस्था बना रहा है।'
                : 'Rightoz is building a more transparent dairy ecosystem with fresh milk, farmer partnerships, careful handling, and subscription-led convenience for families'}
            </p>
          </div>

          <div>
            <h3 className="font-display text-2xl text-[var(--color-cream)]">{isHindi ? 'जानें' : 'Explore'}</h3>
            <div className="mt-5 space-y-3">
              {quickLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="block text-sm text-[#d7e2d0] transition hover:text-white"
                >
                  {link.name}
                </a>
              ))}
            </div>
          </div>

          <div>
            <h3 className="font-display text-2xl text-[var(--color-cream)]">{isHindi ? 'संपर्क' : 'Contact'}</h3>
            <div className="mt-5 space-y-4 text-sm text-[#d7e2d0]">
              <div className="flex items-start gap-3">
                <MapPinIcon className="mt-0.5 h-5 w-5 shrink-0 text-[#b8cfaa]" />
                <span>{isHindi ? 'जयपुर, राजस्थान, भारत' : 'Jaipur, Rajasthan, India'}</span>
              </div>
              <div className="flex items-start gap-3">
                <PhoneIcon className="mt-0.5 h-5 w-5 shrink-0 text-[#b8cfaa]" />
                <a href="tel:+919114491158" className="transition hover:text-white">
                  +91 91144 91158
                </a>
              </div>
              <div className="flex items-start gap-3">
                <EnvelopeIcon className="mt-0.5 h-5 w-5 shrink-0 text-[#b8cfaa]" />
                <a href="mailto:info@rightoz.com" className="transition hover:text-white">
                  info@rightoz.com
                </a>
              </div>
              <div className="flex flex-wrap gap-2">
                <a
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex rounded-full border border-white/10 px-4 py-2 text-sm text-[#d7e2d0] transition hover:bg-white/10 hover:text-white"
                >
                  Instagram
                </a>
                <a
                  href={INSTAGRAM_REEL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex rounded-full border border-white/10 px-4 py-2 text-sm text-[#d7e2d0] transition hover:bg-white/10 hover:text-white"
                >
                  {isHindi ? 'वीडियो देखें' : 'Watch the Reel'}
                </a>
              </div>
              <a
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center rounded-full border border-white/10 px-4 py-2 text-sm text-[#d7e2d0] transition hover:bg-white/10 hover:text-white"
              >
                <DevicePhoneMobileIcon className="mr-2 h-4 w-4" />
                {isHindi ? 'Android फोन के लिए' : 'For Android phone'}
              </a>
              <a
                href={IOS_APP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center rounded-full border border-white/10 px-4 py-2 text-sm text-[#d7e2d0] transition hover:bg-white/10 hover:text-white"
              >
                <DevicePhoneMobileIcon className="mr-2 h-4 w-4" />
                {isHindi ? 'iPhone के लिए' : 'For iPhone'}
              </a>
              <a
                href={WEB_APP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center rounded-full border border-white/10 px-4 py-2 text-sm text-[#d7e2d0] transition hover:bg-white/10 hover:text-white"
              >
                <GlobeAltIcon className="mr-2 h-4 w-4" />
                {isHindi ? 'ब्राउज़र में खोलें' : 'Open in browser'}
              </a>
            </div>
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-5 border-t border-white/10 pt-8 text-sm text-[#b9c7b3] lg:flex-row lg:items-center lg:justify-between">
          <p>© {currentYear} Rightoz {isHindi ? 'सर्वाधिकार सुरक्षित।' : 'All rights reserved'}</p>
          <div className="flex flex-wrap gap-5">
            <a href="/terms-conditions" className="transition hover:text-white">
              {isHindi ? 'नियम और शर्तें (English)' : 'Terms & Conditions'}
            </a>
            <a href="/refund-returns" className="transition hover:text-white">
              {isHindi ? 'रिफंड और वापसी (English)' : 'Refund & Returns'}
            </a>
            <a href="/privacy-policy" className="transition hover:text-white">
              {isHindi ? 'गोपनीयता नीति (English)' : 'Privacy Policy'}
            </a>
            <a href="/delete-account" className="transition hover:text-white">
              {isHindi ? 'खाता हटाएँ (English)' : 'Delete Account'}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
